// '~/utils' 배럴로 가져오면 utils/common(배럴) → services → constants → 이 파일 → utils/common 으로
// 되돌아오는 순환 참조가 생긴다. (배럴 초기화 도중 재진입해 formatNumber가 undefined가 됨)
// constants 는 utils 배럴에 의존하지 않도록 leaf 파일에서 직접 가져와 사이클을 끊는다.
import formatNumber from '~/utils/common/format/formatNumber';

import { PRODUCT_LIMITS } from './product';

/**
 * 필수 입력 필드
 */
export const PRODUCT_VALIDATION_FIELDS = {
  // 공통 필수 선택 필드
  COMMON_SELECT: [
    'supplierCodes', // 공급사명
    'shelfLifeType', // 기한 정책
    'temperatureType', // 보관 유형
    'mainCategoryCode', // 대분류
    'middleCategoryCode', // 중분류
    'thirdCategoryCode', // 소분류
    'unit', // 단위
    'productType', // 상품 운영 종류
    'kurlyOrderType', // 오더 유형 - 샛별
    'parcelOrderType', // 오더 유형 - 택배
    'massDocumentNum', // 오더 유형 - 대량
  ] as const,

  // 공통 필수 입력 필드
  COMMON_INPUT: [
    'skuName', // 상품명
    'availableDays', // 기한 정책 - 숫자
    'sellAvailableDays', // 판매 가능 일수
    'sellDeadlineDays', // 판매 마감 일수
    'netWeight', // 개당 중량 - 순중량
    'grossWeight', // 개당 중량 - 총중량
    'boxPerQuantity', // 박스당 입수
    'boxNetWeight', // 박스 당 중량 - 순중량
    'boxGrossWeight', // 박스 당 중량 - 총중량
    'length', // 개당 부피 - 길이
    'width', // 개당 부피 - 폭
    'height', // 개당 부피 - 높이
    'boxLength', // 박스 당 부피 - 길이
    'boxWidth', // 박스 당 부피 - 폭
    'boxHeight', // 박스 당 부피 - 높이
  ] as const,

  // FBK 전용 필드
  FBK_ONLY_SELECT: [
    'packingDirection', // 정방향여부
  ] as const,

  FBK_ONLY_INPUT: [
    'skuCode', // 상품코드
  ] as const,

  // 3PL 전용 필드
  '3PL_ONLY_SELECT': [
    'useMethodCode', // 상품 사용여부
  ] as const,

  '3PL_ONLY_INPUT': [] as const,
} as const;

/**
 * OwnerType별 필수 필드 반환 함수
 */
export const getRequiredFieldsByOwnerType = (ownerType: 'FBK' | '3PL') => {
  if (ownerType === 'FBK') {
    return {
      selectFields: [...PRODUCT_VALIDATION_FIELDS.COMMON_SELECT, ...PRODUCT_VALIDATION_FIELDS.FBK_ONLY_SELECT],
      inputFields: [...PRODUCT_VALIDATION_FIELDS.COMMON_INPUT, ...PRODUCT_VALIDATION_FIELDS.FBK_ONLY_INPUT],
    };
  } else {
    return {
      selectFields: [...PRODUCT_VALIDATION_FIELDS.COMMON_SELECT, ...PRODUCT_VALIDATION_FIELDS['3PL_ONLY_SELECT']],
      inputFields: [...PRODUCT_VALIDATION_FIELDS.COMMON_INPUT, ...PRODUCT_VALIDATION_FIELDS['3PL_ONLY_INPUT']],
    };
  }
};

/**
 * '상품 운영 종류'에 따른 조건부 필드
 */
export const PRODUCT_CONDITIONAL_FIELDS_BY_PRODUCT_TYPE = {
  // 상품 운영 종류가 '본품'일 때만 필수
  ORIGINAL_ONLY: ['alternativeCode'],
  // 상품 운영 종류가 '대품'일 때만 필수
  ALTERNATIVE_ONLY: ['productCode'],
} as const;

/**
 * 최대값 검증 필드
 */
export const PRODUCT_MAX_VALUE_FIELDS = {
  // 99,999 이하
  POLICY_DAYS: {
    fields: ['availableDays', 'sellAvailableDays', 'sellDeadlineDays'],
    maxValue: PRODUCT_LIMITS.INPUT_VALUES.POLICY_DAYS,
    message: `${formatNumber(PRODUCT_LIMITS.INPUT_VALUES.POLICY_DAYS)} 이하로 입력해주세요.`,
  },
  // 20,000 이하
  BOX_PER_QTY: {
    fields: ['boxPerQuantity'],
    maxValue: PRODUCT_LIMITS.INPUT_VALUES.BOX_PER_QTY,
    message: `${formatNumber(PRODUCT_LIMITS.INPUT_VALUES.BOX_PER_QTY)} 이하로 입력해주세요.`,
  },
} as const;

/**
 * 자릿수 제한 검증 필드
 */
export const PRODUCT_DIGIT_LIMIT_FIELDS = {
  // 15자리까지
  WEIGHT_DIMENSION: {
    fields: [
      'netWeight',
      'grossWeight',
      'length',
      'width',
      'height',
      'boxNetWeight',
      'boxGrossWeight',
      'boxLength',
      'boxWidth',
      'boxHeight',
    ],
    maxDigits: PRODUCT_LIMITS.INPUT_DIGITS.WEIGHT_DIMENSION,
    message: `${PRODUCT_LIMITS.INPUT_DIGITS.WEIGHT_DIMENSION}자리 이하로 입력해주세요.`,
  },
  // 17자리까지 (계산된 부피)
  VOLUME: {
    fields: ['cbm', 'boxCbm'],
    maxDigits: PRODUCT_LIMITS.INPUT_DIGITS.VOLUME,
    message: `부피(길이x폭x높이)가 ${PRODUCT_LIMITS.INPUT_DIGITS.VOLUME}자 이하여야 합니다.`,
  },
  // 9자리까지
  SAFETY_STOCK_DAYS: {
    fields: ['stockSafeDate'],
    maxDigits: PRODUCT_LIMITS.INPUT_DIGITS.SAFETY_STOCK_DAYS,
    message: `${PRODUCT_LIMITS.INPUT_DIGITS.SAFETY_STOCK_DAYS}자리 이하로 입력해주세요.`,
  },
} as const;

/**
 * 부피 계산 필드 매핑
 */
export const VOLUME_CALCULATION_FIELDS = {
  cbm: {
    dimensionFields: ['length', 'width', 'height'],
    volumeField: 'cbm',
  },
  boxCbm: {
    dimensionFields: ['boxLength', 'boxWidth', 'boxHeight'],
    volumeField: 'boxCbm',
  },
} as const;

/**
 * 자릿수 제한 검증용: 부피를 제외한 필드들만 반환
 */
export const getDigitLimitFieldsExcludingVolume = () => {
  const { VOLUME: _VOLUME, ...fieldsWithoutVolume } = PRODUCT_DIGIT_LIMIT_FIELDS;
  return fieldsWithoutVolume;
};

/**
 * 0 이상의 정수 입력 제한 필드
 */
export const PRODUCT_INTEGER_FIELDS = ['stockSafeDate'] as const;

/**
 * 1 이상의 정수 입력 제한 필드 (일수, 중량, 부피, 박스당 입수)
 */
export const PRODUCT_POSITIVE_INTEGER_FIELDS = [
  'availableDays',
  'sellAvailableDays',
  'sellDeadlineDays',
  'netWeight',
  'grossWeight',
  'boxNetWeight',
  'boxGrossWeight',
  'length',
  'width',
  'height',
  'boxPerQuantity',
  'boxLength',
  'boxWidth',
  'boxHeight',
] as const;

export type ProductRequiredSelectField =
  | (typeof PRODUCT_VALIDATION_FIELDS.COMMON_SELECT)[number]
  | (typeof PRODUCT_VALIDATION_FIELDS.FBK_ONLY_SELECT)[number]
  | (typeof PRODUCT_VALIDATION_FIELDS)['3PL_ONLY_SELECT'][number];

export type ProductRequiredInputField =
  | (typeof PRODUCT_VALIDATION_FIELDS.COMMON_INPUT)[number]
  | (typeof PRODUCT_VALIDATION_FIELDS.FBK_ONLY_INPUT)[number]
  | (typeof PRODUCT_VALIDATION_FIELDS)['3PL_ONLY_INPUT'][number];

export type ProductValidationField = ProductRequiredSelectField | ProductRequiredInputField;

export type ProductConditionalField =
  | (typeof PRODUCT_CONDITIONAL_FIELDS_BY_PRODUCT_TYPE.ORIGINAL_ONLY)[number]
  | (typeof PRODUCT_CONDITIONAL_FIELDS_BY_PRODUCT_TYPE.ALTERNATIVE_ONLY)[number];
